/// <reference types="../../vite-env" />
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { buildFirebaseConfig, shouldUseEmulator, EMULATOR_PORTS } from '../../../../shared/lib/firebaseConfig';

const isLocalhost = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const emulatorEnv = {
  isDev: !!import.meta.env.DEV,
  useEmulator: import.meta.env.VITE_USE_EMULATOR === 'true',
  isLocalhost,
};

const firebaseConfig = buildFirebaseConfig({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}, emulatorEnv);

// Validate Firebase config
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error('[Microsite] Firebase configuration incomplete:', {
    hasApiKey: !!firebaseConfig.apiKey,
    hasProjectId: !!firebaseConfig.projectId,
    hasAuthDomain: !!firebaseConfig.authDomain
  });
}

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
const db = getFirestore(app);

// Handle IndexedDB errors gracefully
// Firestore uses IndexedDB for offline persistence by default
// For the microsite (public-facing, no offline needs), we can handle errors but don't need to disable persistence
if (typeof window !== 'undefined') {
  // Listen for IndexedDB errors and log them
  window.addEventListener('error', (event) => {
    if (event.error && (
      event.error.message?.includes('IndexedDB') ||
      event.error.message?.includes('Indexed Database') ||
      event.error.name === 'UnknownError'
    )) {
      console.warn('[Microsite] IndexedDB error detected:', event.error.message);
      console.warn('[Microsite] This may affect offline capabilities but should not prevent the page from loading.');
      console.warn('[Microsite] If the page fails to load, try refreshing or clearing browser storage.');
    }
  });

  // Handle unhandled promise rejections related to IndexedDB
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (reason && (
      reason.message?.includes('IndexedDB') ||
      reason.message?.includes('Indexed Database') ||
      reason.name === 'UnknownError'
    )) {
      console.warn('[Microsite] IndexedDB promise rejection:', reason.message);
      // Prevent the error from propagating - Firestore will fall back to network-only mode
      event.preventDefault();
    }
  });
}

if (shouldUseEmulator(emulatorEnv)) {
  try {
    connectFirestoreEmulator(db, 'localhost', EMULATOR_PORTS.firestore);
    // eslint-disable-next-line no-console
    console.log(`[Microsite] Connected to Firestore emulator on localhost:${EMULATOR_PORTS.firestore}`);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[Microsite] Failed to connect to Firestore emulator:', error);
  }
}

export { app, db };
